import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Stack,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarIcon,
  Event as EventIcon,
  PlayCircleOutline as StartIcon,
  EmojiEvents as EndIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import tournamentService from '../services/tournamentService';

// Mock Calendar Data
const mockCalendarEvents = [
  {
    id: 1,
    title: 'Inicio de Inscripciones',
    date: '2024-07-15T09:00:00',
    description: 'Se abren las inscripciones para el torneo.',
    icon: <EventIcon color="primary" />
  },
  {
    id: 2,
    title: 'Cierre de Inscripciones',
    date: '2024-07-25T23:59:00',
    description: 'Último día para inscribirse.',
    icon: <EventIcon color="secondary" />
  },
  {
    id: 3,
    title: 'Generación del Bracket',
    date: '2024-07-26T12:00:00',
    description: 'Se publicará el bracket inicial del torneo.',
    icon: <EventIcon color="info" />
  },
  {
    id: 4,
    title: 'Inicio del Torneo',
    date: '2024-07-28T10:00:00',
    description: 'Comienzan las primeras rondas.',
    icon: <StartIcon color="success" />
  },
    {
    id: 5,
    title: 'Final del Torneo',
    date: '2024-08-10T18:00:00',
    description: 'Gran final y ceremonia de premiación.',
    icon: <EndIcon sx={{ color: '#c89e38' }} />
  },
];

const TournamentCalendarPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTournamentDetails = async () => {
      try {
        setLoading(true);
        // Use mock data directly for now, but keep API call structure
        const mockTournamentData = {
          id: id,
          name: `Torneo de Prueba ${id}`,
        };
        setTournament(mockTournamentData);
        // const data = await tournamentService.getTournamentById(id);
        // setTournament(data);
      } catch (error) {
        console.error('Error fetching tournament details:', error);
        setError('Error al cargar los detalles del torneo');
      } finally {
        setLoading(false);
      }
    };

    fetchTournamentDetails();
  }, [id]);

  const handleBack = () => {
    navigate(`/tournaments/${id}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !tournament) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh', gap: 2 }}>
        <Alert severity="error">{error || 'No se encontraron datos del torneo'}</Alert>
        <Button variant="contained" onClick={() => navigate(`/tournaments/${id}`)} startIcon={<ArrowBackIcon />}>
          Volver al Torneo
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 1 }, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '90%', maxWidth: '1000px', mx: 'auto', bgcolor: 'transparent' }}>
      <Stack spacing={2} sx={{ width: '100%', bgcolor: 'transparent' }}>
        {/* Header */}
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconButton onClick={handleBack} sx={{ width: 36, height: 36, bgcolor: 'action.hover', color: 'text.primary', '&:hover': { bgcolor: 'primary.main', color: 'white', transform: 'scale(1.1)' }, transition: 'all 0.2s ease-in-out' }}>
                <ArrowBackIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <Box>
                <Typography variant="h6" fontWeight="bold">Calendario del Torneo</Typography>
                <Typography variant="body2" color="text.secondary">{tournament.name}</Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Calendar Events List */}
        <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <List disablePadding>
            {mockCalendarEvents.map((event, index) => (
              <React.Fragment key={event.id}>
                <ListItem sx={{ alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ mt: 0.5, minWidth: 40 }}>
                    {event.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={event.title}
                    secondary={
                      <React.Fragment>
                        <Typography component="span" variant="body2" color="text.primary" sx={{ display: 'block' }}>
                          {format(new Date(event.date), "EEEE d 'de' MMMM, yyyy '-' HH:mm 'hs'", { locale: es })}
                        </Typography>
                        {event.description}
                      </React.Fragment>
                    }
                    primaryTypographyProps={{ fontWeight: 'medium', mb: 0.5 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
                {index < mockCalendarEvents.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
          </List>
        </Paper>

        {/* Back Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="outlined" onClick={handleBack} startIcon={<ArrowBackIcon />} sx={{ borderRadius: 1.5, textTransform: 'none', borderColor: '#c89e38', color: '#c89e38', '&:hover': { borderColor: '#b38d2d', bgcolor: 'rgba(200, 158, 56, 0.04)' } }}>
            Volver al Torneo
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default TournamentCalendarPage; 